import axios from 'axios';

const isLocalDev = typeof window !== 'undefined'
  && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

export const PMI_SERVICE_BASE = isLocalDev
  ? 'http://localhost:8101'
  : '/pmi-api';

// PMI API calls route through the actor-management proxy in production to avoid
// the edge-nginx 405 on POST/PUT/PATCH to /pmi-api/ (same workaround already used
// for logistics/map/customer services). Media & uploads still use PMI_SERVICE_BASE.
// This covers ALL PMI pages incl. Hospitality and Restaurant. Local dev is direct.
export const PMI_API_ROOT = isLocalDev
  ? 'http://localhost:8101/api/v1'
  : '/actor-management/api/v1/pmi-platform/api/v1';

const client = axios.create({
  baseURL: `${PMI_API_ROOT}/pmi`,
  headers: { 'Content-Type': 'application/json' },
});
export { client as pmiClient };

client.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const pmiApi = {
  // Customer portal scope is resolved from the authenticated identity by PMI;
  // never pass a tenant id from the browser for this check.
  getPortalMe: async () => (await client.get('/portal/me')).data,
  getCustomerProductWorkflow: async () => (await client.get('/customer/products/workflow')).data,
  createCustomerProduct: async (payload) => (await client.post('/customer/products', payload)).data,
  updateCustomerProduct: async (id, payload) => (await client.patch(`/customer/products/${encodeURIComponent(id)}`, payload)).data,
  transitionCustomerProduct: async (id, payload) => (await client.post(`/customer/products/${encodeURIComponent(id)}/transition`, payload)).data,
  getCustomerProductEvents: async (id) => (await client.get(`/customer/products/${encodeURIComponent(id)}/events`)).data,
  getCustomerWidgetStudio: async (widgetKey) => (
    await client.get(`/portal/widgets/${encodeURIComponent(widgetKey)}/studio`)
  ).data,
  getAdminWidgetStudio: async (widgetKey, tenantId) => (
    await client.get(`/admin/widget-pricing/${encodeURIComponent(widgetKey)}/${encodeURIComponent(tenantId)}/studio`)
  ).data,
  getCustomerWidgetPricingSources: async (widgetKey) => (
    await client.get(`/portal/widgets/${encodeURIComponent(widgetKey)}/pricing-sources`)
  ).data,
  getAdminPricingBusinesses: async (widgetKey) => (
    await client.get(`/admin/widget-pricing/${encodeURIComponent(widgetKey)}/businesses`)
  ).data,
  getAdminPricingSources: async (widgetKey, tenantId) => (
    await client.get(`/admin/widget-pricing/${encodeURIComponent(widgetKey)}/${encodeURIComponent(tenantId)}`)
  ).data,
  saveAdminWidgetStudio: async (widgetKey, tenantId, payload) => (
    await client.put(`/admin/widget-pricing/${encodeURIComponent(widgetKey)}/${encodeURIComponent(tenantId)}/studio`, payload)
  ).data,
  saveCustomerWidgetStudio: async (widgetKey, payload) => (
    await client.put(`/portal/widgets/${encodeURIComponent(widgetKey)}/studio`, payload)
  ).data,
  getWidgetCatalog: async () => (await client.get('/widgets/catalog')).data,
  // Platform-admin controls for scoped hospitality/restaurant connection setup.
  getAdminDomainConnections: async (params) => (await client.get('/admin/domain-connections', { params })).data,
  saveAdminDomainConnection: async (payload) => (await client.post('/admin/domain-connections', payload)).data,
  disconnectAdminDomainConnection: async (connectionId, params) => (
    await client.delete(`/admin/domain-connections/${encodeURIComponent(connectionId)}`, { params })
  ).data,
  getWidgetInstallations: async ({ tenantId = 'default', shopId, includeInactive = true } = {}) => (
    await client.get('/widgets/installations', {
      params: {
        tenant_id: tenantId,
        ...(shopId ? { shop_id: shopId } : {}),
        include_inactive: includeInactive,
      },
    })
  ).data,
  createWidgetInstallation: async (payload) => (
    await client.post('/widgets/installations', payload)
  ).data,
  updateWidgetInstallation: async (id, payload) => (
    await client.patch(`/widgets/installations/${id}`, payload)
  ).data,
  // ── Sales Channel connections (Products widget) ─────────────────────
  getChannels: async (tenantId = 'default') => (
    await client.get('/channels', { params: { tenant_id: tenantId } })
  ).data,
  connectChannel: async (payload) => (
    await client.post('/channels/connect', payload)
  ).data,
  disconnectChannel: async (channel, tenantId = 'default') => (
    await client.post(`/channels/${channel}/disconnect`, null, { params: { tenant_id: tenantId } })
  ).data,
  assistChannel: async (channel, question = '') => (
    await client.post(`/channels/${channel}/assist`, { question })
  ).data,
  assistWidgetGuide: async (widgetKey, payload) => (
    await client.post(`/widgets/${encodeURIComponent(widgetKey)}/guide`, payload)
  ).data,
  // Owner-tenant only: pull ERP products/spare-parts into PMI (no token).
  syncFromErp: async (sources = ['products', 'spare_parts']) => (
    await client.post('/sync', { sources })
  ).data,
  // Push PMI products out to a connected channel (Shopify supported today).
  publishChannel: async (channel, { tenantId = 'default', productIds = null } = {}) => (
    await client.post(`/channels/${channel}/publish`, { tenant_id: tenantId, product_ids: productIds })
  ).data,
  // ── Channel connector platform (ADR-0010): queued publishing, webhooks, OAuth ──
  getChannelConnectors: async () => (await client.get('/channel-platform/connectors')).data,
  countActiveProducts: async (tenantId = 'default') => (
    (await client.get('/products', { params: { tenant_id: tenantId, limit: 1 } })).data?.total || 0
  ),
  queueChannelPublish: async (channel, { tenantId = 'default', productIds = null } = {}) => (
    await client.post(`/channel-platform/${encodeURIComponent(channel)}/jobs/publish`, { tenant_id: tenantId, product_ids: productIds })
  ).data,
  getChannelJobs: async ({ tenantId = 'default', channel, status, limit = 200 } = {}) => (
    await client.get('/channel-platform/jobs', {
      params: { tenant_id: tenantId, limit, ...(channel ? { channel } : {}), ...(status ? { status } : {}) },
    })
  ).data,
  retryChannelJob: async (jobId) => (await client.post(`/channel-platform/jobs/${encodeURIComponent(jobId)}/retry`)).data,
  getStockHistory: async (productId) => (await client.get(`/products/${encodeURIComponent(productId)}/stock-history`)).data,
  adjustStock: async (productId, payload) => (await client.post(`/products/${encodeURIComponent(productId)}/stock-adjust`, payload)).data,
  listBuyers: async (tenantId = 'default') => (await client.get('/buyers', { params: { tenant_id: tenantId } })).data,
  getBuyer: async (key, tenantId = 'default') => (await client.get('/buyers/detail', { params: { key, tenant_id: tenantId } })).data,
  createManualOrder: async (payload, tenantId = 'default') => (
    await client.post('/manual-orders', payload, { params: { tenant_id: tenantId } })
  ).data,
  getShopInsights: async ({ tenantId = 'default', days = 30 } = {}) => (
    await client.get('/shop-insights', { params: { tenant_id: tenantId, days } })
  ).data,
  setChannelApiMode: async (channel, { tenantId = 'default', mode }) => (
    await client.put(`/channel-platform/${encodeURIComponent(channel)}/api-mode`, { tenant_id: tenantId, mode })
  ).data,
  setChannelWebhookSecret: async (channel, { tenantId = 'default', webhookSecret = null } = {}) => (
    await client.put(`/channel-platform/${encodeURIComponent(channel)}/webhook-secret`, { tenant_id: tenantId, webhook_secret: webhookSecret })
  ).data,
  getChannelWebhookEvents: async ({ tenantId = 'default', limit = 100 } = {}) => (
    await client.get('/channel-platform/webhooks', { params: { tenant_id: tenantId, limit } })
  ).data,
  getChannelFeeds: async (tenantId = 'default') => (
    await client.get('/channel-platform/feeds', { params: { tenant_id: tenantId } })
  ).data,
  createChannelFeed: async ({ tenantId = 'default', format, language = 'de' }) => (
    await client.post('/channel-platform/feeds', { tenant_id: tenantId, format, language })
  ).data,
  rotateChannelFeed: async (feedId) => (await client.post(`/channel-platform/feeds/${encodeURIComponent(feedId)}/rotate`)).data,
  deleteChannelFeed: async (feedId) => (await client.delete(`/channel-platform/feeds/${encodeURIComponent(feedId)}`)).data,
  startOrderReturn: async (recordId, payload) => (
    await client.post(`/channel-platform/orders/${encodeURIComponent(recordId)}/returns`, payload)
  ).data,
  stepOrderReturn: async (recordId, returnId, action, payload = {}) => (
    await client.post(`/channel-platform/orders/${encodeURIComponent(recordId)}/returns/${encodeURIComponent(returnId)}/${action}`, payload)
  ).data,
  fulfilOrder: async (recordId, payload) => (
    await client.post(`/channel-platform/orders/${encodeURIComponent(recordId)}/fulfil`, payload)
  ).data,
  syncProductToChannels: async (productId, tenantId = 'default') => (
    await client.post(`/channel-platform/products/${encodeURIComponent(productId)}/sync`, { tenant_id: tenantId })
  ).data,
  reprocessChannelEvent: async (eventId) => (
    await client.post(`/channel-platform/webhooks/${encodeURIComponent(eventId)}/reprocess`)
  ).data,
  startChannelOAuth: async (channel, tenantId = 'default') => (
    await client.post(`/channel-platform/${encodeURIComponent(channel)}/oauth/start`, { tenant_id: tenantId })
  ).data,
  previewChannelPrice: async (payload) => (await client.post('/channel-platform/price-preview', payload)).data,
  // Customer-scoped product channel integration. The server derives tenant scope from the customer token.
  getCustomerChannels: async () => (await client.get('/customer/channels')).data,
  getCustomerChannelProducts: async () => (await client.get('/customer/channels/products')).data,
  reconcileCustomerShopifyProduct: async (payload) => (await client.post('/customer/channels/shopify/reconcile', payload)).data,
  connectCustomerChannel: async (payload) => (await client.post('/customer/channels/connect', payload)).data,
  disconnectCustomerChannel: async (channel) => (await client.post(`/customer/channels/${channel}/disconnect`)).data,
  // ── Customers (PMI tenant registry) ─────────────────────────────────
  getCustomers: async ({ search, includeArchived = false } = {}) => (
    await client.get('/customers', {
      params: {
        ...(search ? { search } : {}),
        include_archived: includeArchived,
      },
    })
  ).data,
  getCustomer: async (id) => (await client.get(`/customers/${id}`)).data,
  searchActors: async (q) => (await client.get('/customers/actor-search', { params: { q } })).data,
  createCustomer: async (payload) => (await client.post('/customers', payload)).data,
  updateCustomer: async (id, payload) => (await client.patch(`/customers/${id}`, payload)).data,
  // Customer users (the people who log into the customer's portal)
  getCustomerUsers: async (customerId) => (await client.get(`/customers/${customerId}/users`)).data,
  addCustomerUser: async (customerId, payload) => (await client.post(`/customers/${customerId}/users`, payload)).data,
  updateCustomerUser: async (customerId, userId, payload) => (
    await client.patch(`/customers/${customerId}/users/${userId}`, payload)
  ).data,
  removeCustomerUser: async (customerId, userId) => (await client.delete(`/customers/${customerId}/users/${userId}`)).data,
  getApiCredentials: async ({ tenantId = 'default', includeRevoked = false } = {}) => (
    await client.get('/api-credentials', { params: { tenant_id: tenantId, include_revoked: includeRevoked } })
  ).data,
  getApiCredentialAudit: async ({ tenantId = 'default', limit = 100 } = {}) => (
    await client.get('/api-credentials/audit', { params: { tenant_id: tenantId, limit } })
  ).data,
  getApiScopes: async () => (await client.get('/api-credentials/scopes')).data,
  createApiCredential: async (payload) => (await client.post('/api-credentials', payload)).data,
  rotateApiCredential: async (id, tenantId) => (
    await client.post(`/api-credentials/${id}/rotate`, null, { params: { tenant_id: tenantId } })
  ).data,
  revokeApiCredential: async (id, tenantId) => (
    await client.post(`/api-credentials/${id}/revoke`, null, { params: { tenant_id: tenantId } })
  ).data,
  getWidgetEntitlement: async ({ widgetKey, tenantId = 'default', shopId = 'all' }) => (
    await client.get(`/widgets/entitlements/${widgetKey}`, {
      params: { tenant_id: tenantId, shop_id: shopId },
    })
  ).data,
  getBookingServices: async ({ tenantId = 'default', shopId = 'all', includeInactive = true } = {}) => (
    await client.get('/booking/services', {
      params: { tenant_id: tenantId, shop_id: shopId, include_inactive: includeInactive },
    })
  ).data,
  createBookingService: async (payload) => (await client.post('/booking/services', payload)).data,
  updateBookingService: async (id, { tenantId, shopId, ...payload }) => (
    await client.patch(`/booking/services/${id}`, payload, {
      params: { tenant_id: tenantId, shop_id: shopId },
    })
  ).data,
  getBookingResources: async ({ tenantId = 'default', shopId = 'all', includeInactive = true } = {}) => (
    await client.get('/booking/resources', {
      params: { tenant_id: tenantId, shop_id: shopId, include_inactive: includeInactive },
    })
  ).data,
  createBookingResource: async (payload) => (await client.post('/booking/resources', payload)).data,
  updateBookingResource: async (id, { tenantId, shopId, ...payload }) => (
    await client.patch(`/booking/resources/${id}`, payload, {
      params: { tenant_id: tenantId, shop_id: shopId },
    })
  ).data,
  getBookingAvailabilityRules: async ({ resourceId, tenantId, shopId }) => (
    await client.get(`/booking/resources/${resourceId}/availability-rules`, {
      params: { tenant_id: tenantId, shop_id: shopId },
    })
  ).data,
  createBookingAvailabilityRule: async ({ resourceId, tenantId, shopId, ...payload }) => (
    await client.post(`/booking/resources/${resourceId}/availability-rules`, payload, {
      params: { tenant_id: tenantId, shop_id: shopId },
    })
  ).data,
  getPmiBookings: async ({ tenantId = 'default', shopId = 'all', status } = {}) => (
    await client.get('/booking/bookings', {
      params: { tenant_id: tenantId, shop_id: shopId, ...(status ? { status } : {}) },
    })
  ).data,
  getPmiBookingAvailability: async ({ tenantId, shopId, serviceId, resourceId, dateFrom, dateTo, quantity = 1 }) => (
    await client.get('/booking/availability', {
      params: {
        tenant_id: tenantId,
        shop_id: shopId,
        service_id: serviceId,
        resource_id: resourceId,
        date_from: dateFrom,
        date_to: dateTo,
        quantity,
      },
    })
  ).data,
  createRepairBooking: async (payload) => (
    await client.post('/booking/repair-bookings', payload)
  ).data,
  getHospitalitySummary: async ({ tenantId = 'default', shopId = 'all' } = {}) => (
    await client.get('/hospitality/summary', { params: { tenant_id: tenantId, shop_id: shopId } })
  ).data,
  getHospitalityReport: async ({ tenantId = 'default', shopId = 'all', dateFrom, dateTo } = {}) => (
    await client.get('/hospitality/reports', { params: { tenant_id: tenantId, shop_id: shopId, date_from: dateFrom, date_to: dateTo } })
  ).data,
  getHospitalityProperties: async ({ tenantId = 'default', shopId = 'all' } = {}) => (
    await client.get('/hospitality/properties', { params: { tenant_id: tenantId, shop_id: shopId } })
  ).data,
  createHospitalityProperty: async (payload) => (
    await client.post('/hospitality/properties', payload)
  ).data,
  updateHospitalityProperty: async (id, { tenantId, shopId, ...payload }) => (
    await client.patch(`/hospitality/properties/${id}`, payload, {
      params: { tenant_id: tenantId, shop_id: shopId },
    })
  ).data,
  getHospitalityOperationsToday: async ({ tenantId = 'default', shopId = 'all' } = {}) => (
    await client.get('/hospitality/operations/today', { params: { tenant_id: tenantId, shop_id: shopId } })
  ).data,
  getHospitalityPropertyOnboarding: async (id, { tenantId = 'default', shopId = 'all' } = {}) => (
    await client.get(`/hospitality/properties/${id}/onboarding`, {
      params: { tenant_id: tenantId, shop_id: shopId },
    })
  ).data,
  getHospitalityPropertyCommandCenter: async (id, { tenantId = 'default', shopId = 'all' } = {}) => (
    await client.get(`/hospitality/properties/${id}/command-center`, {
      params: { tenant_id: tenantId, shop_id: shopId },
    })
  ).data,
  getHospitalityOwnerDashboard: async (ownerActorId, { tenantId = 'default', shopId = 'all' } = {}) => (
    await client.get(`/hospitality/owners/${ownerActorId}/dashboard`, {
      params: { tenant_id: tenantId, shop_id: shopId },
    })
  ).data,
  getHospitalityUnits: async ({ tenantId = 'default', shopId = 'all', propertyId } = {}) => (
    await client.get('/hospitality/units', {
      params: { tenant_id: tenantId, shop_id: shopId, ...(propertyId ? { property_id: propertyId } : {}) },
    })
  ).data,
  createHospitalityUnit: async (payload) => (
    await client.post('/hospitality/units', payload)
  ).data,
  updateHospitalityUnit: async (id, { tenantId, shopId, ...payload }) => (
    await client.patch(`/hospitality/units/${id}`, payload, {
      params: { tenant_id: tenantId, shop_id: shopId },
    })
  ).data,
  getHospitalityTasks: async ({ tenantId = 'default', shopId = 'all', status } = {}) => (
    await client.get('/hospitality/tasks', {
      params: { tenant_id: tenantId, shop_id: shopId, ...(status ? { status } : {}) },
    })
  ).data,
  createHospitalityTask: async (payload) => (
    await client.post('/hospitality/tasks', payload)
  ).data,
  getHospitalityTeam: async ({ tenantId = 'default', shopId = 'all' } = {}) => (
    await client.get('/hospitality/team', { params: { tenant_id: tenantId, shop_id: shopId } })
  ).data,
  updateHospitalityTask: async (id, { tenantId, shopId, ...payload }) => (
    await client.patch(`/hospitality/tasks/${id}`, payload, {
      params: { tenant_id: tenantId, shop_id: shopId },
    })
  ).data,
  transitionHospitalityTask: async (id, { tenantId, shopId, ...payload }) => (
    await client.post(`/hospitality/tasks/${id}/transition`, payload, {
      params: { tenant_id: tenantId, shop_id: shopId },
    })
  ).data,
  getHospitalityCommunications: async ({ tenantId = 'default', shopId = 'all', reservationId } = {}) => (
    await client.get('/hospitality/communications', {
      params: { tenant_id: tenantId, shop_id: shopId, ...(reservationId ? { reservation_id: reservationId } : {}) },
    })
  ).data,
  createHospitalityCommunication: async (payload) => (
    await client.post('/hospitality/communications', payload)
  ).data,
  updateHospitalityCommunication: async (id, { tenantId, shopId, ...payload }) => (
    await client.patch(`/hospitality/communications/${id}`, payload, {
      params: { tenant_id: tenantId, shop_id: shopId },
    })
  ).data,
  transitionHospitalityCommunication: async (id, { tenantId, shopId, ...payload }) => (
    await client.post(`/hospitality/communications/${id}/transition`, payload, {
      params: { tenant_id: tenantId, shop_id: shopId },
    })
  ).data,
  getHospitalityReservations: async ({ tenantId = 'default', shopId = 'all', status } = {}) => (
    await client.get('/hospitality/reservations', {
      params: { tenant_id: tenantId, shop_id: shopId, ...(status ? { status } : {}) },
    })
  ).data,
  createHospitalityReservation: async (payload) => (
    await client.post('/hospitality/reservations', payload)
  ).data,
  updateHospitalityReservation: async (id, { tenantId, shopId, ...payload }) => (
    await client.patch(`/hospitality/reservations/${id}`, payload, {
      params: { tenant_id: tenantId, shop_id: shopId },
    })
  ).data,
  transitionHospitalityReservation: async (id, { tenantId, shopId, ...payload }) => (
    await client.post(`/hospitality/reservations/${id}/transition`, payload, {
      params: { tenant_id: tenantId, shop_id: shopId },
    })
  ).data,
  getHospitalityReservationEvents: async (id, { tenantId = 'default', shopId = 'all' } = {}) => (
    await client.get(`/hospitality/reservations/${id}/events`, {
      params: { tenant_id: tenantId, shop_id: shopId },
    })
  ).data,
  getHospitalityPeople: async ({ tenantId = 'default', shopId = 'all' } = {}) => (
    await client.get('/hospitality/people', {
      params: { tenant_id: tenantId, shop_id: shopId },
    })
  ).data,
  getHospitalityCorporateAccounts: async ({ tenantId = 'default', shopId = 'all' } = {}) => (
    await client.get('/hospitality/corporate-accounts', {
      params: { tenant_id: tenantId, shop_id: shopId },
    })
  ).data,
  createHospitalityCorporateAccount: async (payload) => (
    await client.post('/hospitality/corporate-accounts', payload)
  ).data,
  updateHospitalityCorporateAccount: async (id, { tenantId, shopId, ...payload }) => (
    await client.patch(`/hospitality/corporate-accounts/${id}`, payload, {
      params: { tenant_id: tenantId, shop_id: shopId },
    })
  ).data,
  // Price a stay with the room's seasonal rates (per night, by season, plus cleaning).
  getHospitalityStayPrice: async ({ tenantId = 'default', shopId = 'all', unitId, checkIn, checkOut, corporateAccountId } = {}) => (
    await client.get('/hospitality/rate-rules/price', {
      params: {
        tenant_id: tenantId, shop_id: shopId, unit_id: unitId, check_in_date: checkIn, check_out_date: checkOut,
        ...(corporateAccountId ? { corporate_account_id: corporateAccountId } : {}),
      },
    })
  ).data,
  getHospitalityRateRules: async ({ tenantId = 'default', shopId = 'all', propertyId } = {}) => (
    await client.get('/hospitality/rate-rules', {
      params: { tenant_id: tenantId, shop_id: shopId, ...(propertyId ? { property_id: propertyId } : {}) },
    })
  ).data,
  createHospitalityRateRule: async (payload) => (
    await client.post('/hospitality/rate-rules', payload)
  ).data,
  updateHospitalityRateRule: async (id, { tenantId, shopId, ...payload }) => (
    await client.patch(`/hospitality/rate-rules/${id}`, payload, {
      params: { tenant_id: tenantId, shop_id: shopId },
    })
  ).data,
  getHospitalityChannelListings: async ({ tenantId = 'default', shopId = 'all', propertyId } = {}) => (
    await client.get('/hospitality/channel-listings', {
      params: { tenant_id: tenantId, shop_id: shopId, ...(propertyId ? { property_id: propertyId } : {}) },
    })
  ).data,
  createHospitalityChannelListing: async (payload) => (
    await client.post('/hospitality/channel-listings', payload)
  ).data,
  updateHospitalityChannelListing: async (id, { tenantId, shopId, ...payload }) => (
    await client.patch(`/hospitality/channel-listings/${id}`, payload, {
      params: { tenant_id: tenantId, shop_id: shopId },
    })
  ).data,
  getHospitalityQuotes: async ({ tenantId = 'default', shopId = 'all', status, limit = 100 } = {}) => (
    await client.get('/hospitality/quotes', {
      params: { tenant_id: tenantId, shop_id: shopId, ...(status ? { status } : {}), limit },
    })
  ).data,
  createHospitalityQuote: async (payload) => (
    await client.post('/hospitality/quotes', payload)
  ).data,
  updateHospitalityQuote: async (id, { tenantId, shopId, ...payload }) => (
    await client.patch(`/hospitality/quotes/${id}`, payload, {
      params: { tenant_id: tenantId, shop_id: shopId },
    })
  ).data,
  convertHospitalityQuote: async (id, { tenantId = 'default', shopId = 'all' } = {}) => (
    await client.post(`/hospitality/quotes/${id}/convert`, null, {
      params: { tenant_id: tenantId, shop_id: shopId },
    })
  ).data,
  cancelHospitalityQuote: async (id, { tenantId = 'default', shopId = 'all' } = {}) => (
    await client.post(`/hospitality/quotes/${id}/cancel`, null, {
      params: { tenant_id: tenantId, shop_id: shopId },
    })
  ).data,
  getHospitalityCalendar: async ({ tenantId = 'default', shopId = 'all', start, end, propertyId } = {}) => (
    await client.get('/hospitality/calendar', {
      params: {
        tenant_id: tenantId,
        shop_id: shopId,
        start,
        end,
        ...(propertyId ? { property_id: propertyId } : {}),
      },
    })
  ).data,
  getHospitalityCalendarBlocks: async ({ tenantId = 'default', shopId = 'all', start, end, propertyId } = {}) => (
    await client.get('/hospitality/calendar-blocks', {
      params: { tenant_id: tenantId, shop_id: shopId, start, end, ...(propertyId ? { property_id: propertyId } : {}) },
    })
  ).data,
  createHospitalityCalendarBlock: async ({ tenantId, shopId, ...payload }) => (
    await client.post('/hospitality/calendar-blocks', { tenant_id: tenantId, shop_id: shopId, ...payload })
  ).data,
  deleteHospitalityCalendarBlock: async (id, { tenantId = 'default', shopId = 'all' } = {}) => (
    await client.delete(`/hospitality/calendar-blocks/${id}`, { params: { tenant_id: tenantId, shop_id: shopId } })
  ).data,
  getDashboard: async () => (await client.get('/dashboard')).data,
  getGuidelines: async (activeOnly = false) => (
    await client.get('/guidelines', { params: { active_only: activeOnly } })
  ).data,
  // Resolve a product's PMI guideline + ready (handling) time — the marketplace/order join.
  getGuidelineForProduct: async ({ productId, sku } = {}) => (
    await client.get('/guidelines/for-product', { params: { product_id: productId, sku } })
  ).data,
  createGuideline: async (payload) => (await client.post('/guidelines', payload)).data,
  updateGuideline: async (id, payload) => (await client.put(`/guidelines/${id}`, payload)).data,
  deactivateGuideline: async (id) => (await client.delete(`/guidelines/${id}`)).data,
  updateCapacity: async (id, activeWorkers) => (
    await client.patch(`/guidelines/${id}/capacity`, { active_workers: activeWorkers })
  ).data,
  getQueue: async (includeHistory = false) => (
    await client.get('/queue', { params: { include_history: includeHistory } })
  ).data,
  enqueue: async (payload) => (await client.post('/queue', payload)).data,
  updateWorkItem: async (id, payload) => (await client.patch(`/queue/${id}`, payload)).data,
  cancelWorkItem: async (id) => (await client.delete(`/queue/${id}`)).data,
  simulate: async (payload) => (await client.post('/simulate', payload)).data,
  uploadVideo: async (file) => {
    const body = new FormData();
    body.append('file', file);
    return (await client.post('/videos', body, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data;
  },
  resolveMediaUrl: (url) => {
    if (!url || /^https?:\/\//i.test(url)) return url;
    return `${PMI_SERVICE_BASE}${url.startsWith('/') ? '' : '/'}${url}`;
  },
};

export default pmiApi;
